import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import type {
  MentionSuggestionItem,
  ChipColor,
  ChipOptionValue,
  ChipSourceMeta,
} from "./chipOptionsConfig";
import {
  CHIP_BADGE_CLASSES,
  resolveChipValueId,
  resolveChipValueLabel,
} from "./chipOptionsConfig";

function resolveChipValueMeta(value: ChipOptionValue): ChipSourceMeta {
  if (typeof value === "string") return {};
  return {
    sourceImage: value.sourceImage,
    sourceIconName: value.sourceIconName,
    sourceColor: value.sourceColor,
    sourceName: value.sourceName,
    sourceKind: value.sourceKind,
    dataType: value.dataType,
    isList: value.isList,
  };
}

function formatMentionLabel(id: string, sourceName?: string): string {
  if (!sourceName) return id;
  return `${sourceName} | ${id}`;
}

function SourceNodeAvatar({
  sourceImage,
  sourceIconName,
  sourceColor,
  sourceKind,
  size = "sm",
}: {
  sourceImage?: string;
  sourceIconName?: string;
  sourceColor?: string;
  sourceKind?: "node" | "secret";
  size?: "sm" | "xs";
}) {
  const dim = size === "xs" ? "size-3.5" : "size-4";
  const img = size === "xs" ? "size-2.5" : "size-3";
  if (sourceIconName) {
    return (
      <NoxusIcon icon={sourceIconName} size={size === "xs" ? "xs" : "xsm"} />
    );
  }
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-[3px] shrink-0 overflow-hidden border border-border/40",
        dim
      )}
      style={{ backgroundColor: sourceColor || undefined }}
    >
      {sourceImage ? (
        <img
          src={sourceImage}
          alt=""
          className={cn("object-contain", img)}
          draggable={false}
        />
      ) : sourceKind === "secret" ? (
        <KeyRound
          size={size === "xs" ? 10 : 12}
          strokeWidth={2}
          className="text-indigo-600"
        />
      ) : null}
    </span>
  );
}

function groupItems(items: MentionSuggestionItem[]) {
  const map = new Map<string, MentionSuggestionItem[]>();
  for (const item of items) {
    if (!map.has(item.group)) map.set(item.group, []);
    map.get(item.group)!.push(item);
  }
  return Array.from(map.entries());
}

interface MentionItemListProps {
  items: MentionSuggestionItem[];
  onSelect: (item: MentionSuggestionItem) => void;
  selectedIndex?: number;
  enableKeyboardNav?: boolean;
}

export function MentionItemList({
  items,
  onSelect,
  selectedIndex: externalSelectedIndex,
  enableKeyboardNav = false,
}: MentionItemListProps) {
  const grouped = useMemo(() => groupItems(items), [items]);
  const [internalIndex, setInternalIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedIndex =
    externalSelectedIndex !== undefined ? externalSelectedIndex : internalIndex;
  const isManaged = externalSelectedIndex === undefined && enableKeyboardNav;

  useEffect(() => {
    if (isManaged) setInternalIndex(0);
  }, [items, isManaged]);

  useEffect(() => {
    containerRef.current
      ?.querySelector<HTMLElement>('[data-selected="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isManaged || items.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setInternalIndex((prev) => (prev + 1) % items.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setInternalIndex((prev) => (prev + items.length - 1) % items.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = items[internalIndex];
        if (item) onSelect(item);
      }
    },
    [isManaged, items, internalIndex, onSelect]
  );

  useEffect(() => {
    if (isManaged && containerRef.current) {
      containerRef.current.focus();
    }
  }, [isManaged]);

  if (items.length === 0) {
    return (
      <div className="px-2 py-1.5 text-sm text-muted-foreground">
        No results
      </div>
    );
  }

  let flatIndex = 0;

  return (
    <div
      ref={containerRef}
      tabIndex={isManaged ? 0 : undefined}
      onKeyDown={isManaged ? handleKeyDown : undefined}
      className={isManaged ? "outline-hidden" : undefined}
    >
      {grouped.map(([groupLabel, groupItems]) => (
        <div key={groupLabel} onClick={(e) => e.stopPropagation()}>
          {grouped.length > 1 && (
            <div className="px-2 pt-1.5 pb-1 text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              {groupLabel}
            </div>
          )}
          {groupItems.map((item, index) => {
            const currentIndex = flatIndex;
            flatIndex++;
            const badgeColor = item.color as ChipColor | undefined;
            const badgeClasses = badgeColor
              ? CHIP_BADGE_CLASSES[badgeColor]
              : "";
            const isSelected = currentIndex === selectedIndex;
            const hasSourceMeta = Boolean(
              item.sourceImage ||
              item.sourceColor ||
              item.sourceName ||
              item.sourceKind === "secret"
            );

            return (
              <button
                key={`${groupLabel}-${item.id}`}
                data-selected={isSelected ? "true" : undefined}
                className={cn(
                  "relative flex w-full cursor-default items-center gap-1.5 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
                  isSelected
                    ? badgeClasses || "bg-accent text-accent-foreground"
                    : "hover:bg-accent/50 hover:text-accent-foreground"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(item);
                }}
                type="button"
              >
                {hasSourceMeta ? (
                  <SourceNodeAvatar
                    sourceImage={item.sourceImage}
                    sourceIconName={item.sourceIconName}
                    sourceColor={item.sourceColor}
                    sourceKind={item.sourceKind}
                  />
                ) : (
                  groupItems[0]?.icon && (
                    <span className="shrink-0">{groupItems[0].icon}</span>
                  )
                )}
                <span className="truncate">
                  {formatMentionLabel(item.label || item.id, item.sourceName)}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

type MentionListProps = {
  items: MentionSuggestionItem[];
  command: (attrs: {
    id: string;
    label?: string;
    prefix?: string;
    color?: string;
    icon?: string;
    nodeImage?: string;
    nodeColor?: string;
    nodeName?: string;
    nodeKind?: "node" | "secret";
  }) => void;
};

const MentionList = forwardRef<
  { onKeyDown: (props: { event: React.KeyboardEvent }) => boolean },
  MentionListProps
>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const flatItems = props.items;

  const selectItem = (item: MentionSuggestionItem) => {
    props.command({
      id: item.id,
      label: item.label,
      prefix: item.prefix,
      color: item.color,
      icon: item.prefix || "",
      nodeImage: item.sourceImage,
      nodeColor: item.sourceColor,
      nodeName: item.sourceName,
      nodeKind: item.sourceKind,
    });
  };

  const upHandler = () => {
    setSelectedIndex((selectedIndex + flatItems.length - 1) % flatItems.length);
  };

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % flatItems.length);
  };

  const enterHandler = () => {
    const item = flatItems[selectedIndex];
    if (item) selectItem(item);
  };

  useEffect(() => setSelectedIndex(0), [flatItems]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
      if (event.key === "ArrowUp") {
        upHandler();
        return true;
      }
      if (event.key === "ArrowDown") {
        downHandler();
        return true;
      }
      if (event.key === "Enter") {
        enterHandler();
        return true;
      }
      return false;
    },
  }));

  return (
    <div
      className="bg-popover text-popover-foreground z-50 min-w-40 max-h-64 overflow-y-auto rounded-md border p-1 shadow-md"
      onWheel={(e) => e.stopPropagation()}
    >
      <MentionItemList
        items={flatItems}
        selectedIndex={selectedIndex}
        onSelect={selectItem}
      />
    </div>
  );
});

MentionList.displayName = "MentionList";

export default MentionList;

import { Plus, KeyRound } from "lucide-react";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/shadcn/dropdown-menu";
import type { AdvancedChipOption } from "./chipOptionsConfig";
import { Badge } from "@/components/shadcn/badge";
import { NoxusIcon } from "@/components/UI/NoxusIcon";

interface ChipInsertDropdownProps {
  items: MentionSuggestionItem[];
  onSelect: (item: MentionSuggestionItem) => void;
  isDisabled?: boolean;
}

interface ChipBadgeListProps {
  chipOptions: AdvancedChipOption[];
  activeTags: string[];
  isDisabled?: boolean;
  onSelect: (item: MentionSuggestionItem) => void;
  hiddenGroups?: string[];
}

export function ChipBadgeList({
  chipOptions,
  activeTags,
  isDisabled,
  onSelect,
  hiddenGroups = ["References"],
}: ChipBadgeListProps) {
  const visibleGroups = chipOptions.filter(
    (g) => !hiddenGroups.includes(g.label)
  );

  if (visibleGroups.length === 0) return null;

  return (
    <div className="flex gap-2 mt-1 mb-2 flex-wrap">
      {visibleGroups.flatMap((group) =>
        group.values.map((value) => {
          const id = resolveChipValueId(value);
          const label = resolveChipValueLabel(value);
          const meta = resolveChipValueMeta(value);
          const isActive = activeTags.includes(id);
          return (
            <Badge
              key={`${group.label}-${id}`}
              variant={isActive ? "secondary" : "outline"}
              onClick={
                isDisabled
                  ? undefined
                  : () =>
                      onSelect({
                        id,
                        label,
                        group: group.label,
                        prefix: group.prefix,
                        color: group.color,
                        icon: group.icon,
                        ...meta,
                      })
              }
              aria-disabled={isDisabled}
              className={cn(
                isDisabled
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer hover:opacity-80"
              )}
            >
              {group.icon}
              {label}
            </Badge>
          );
        })
      )}
    </div>
  );
}

export function ChipInsertDropdown({
  items,
  onSelect,
  isDisabled,
}: ChipInsertDropdownProps) {
  const [open, setOpen] = useState(false);

  if (items.length === 0 || isDisabled) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <NoxusTooltip content="Insert variable" asChild>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center justify-center h-6 w-6 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
            type="button"
          >
            <Plus className="size-4" />
          </button>
        </DropdownMenuTrigger>
      </NoxusTooltip>
      <DropdownMenuContent
        align="end"
        className="p-1"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div
          className="max-h-64 overflow-y-auto"
          onWheel={(e) => e.stopPropagation()}
        >
          <MentionItemList
            items={items}
            enableKeyboardNav
            onSelect={(item) => {
              setOpen(false);
              onSelect(item);
            }}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
